export type ItemId =
  // Existing — preserved for save-game / shop continuity.
  | 'gold-ore'
  | 'uranium-ore'
  | 'iron-scrap'
  | 'alien-alloy'
  // New space-side resources (asteroids / mining beam loot).
  | 'raw-quartz'
  | 'ion-ice'
  | 'plasma-crystal'
  | 'stellar-iron'
  // New planetary / lunar resources.
  | 'hydrogen'
  | 'sodium'
  | 'silver'
  | 'aurora-crystal'
  | 'amber-core'
  | 'living-rock'
  | 'lumen-dust';

export type ItemCategory =
  | 'ore'
  | 'material'
  | 'component'
  | 'consumable'
  | 'tool'
  | 'weapon'
  | 'upgrade';

export type ItemRarity = 'common' | 'uncommon' | 'rare' | 'epic';

/** Where the item is naturally sourced from. The trade terminal doesn't
 *  care, but loot tables and future filters can read this to keep the
 *  economy explainable as it scales. */
export type ItemOrigin = 'asteroid' | 'planet' | 'lunar' | 'loot' | 'mixed';

export interface ItemDefinition {
  readonly id: ItemId;
  readonly name: string;
  readonly shortName?: string;
  readonly description: string;
  readonly category: ItemCategory;
  readonly rarity: ItemRarity;
  readonly origin: ItemOrigin;
  /** Credits paid by the trade terminal per unit. Authoritative source of
   *  truth for sale price — UI + economic systems read this directly. */
  readonly sellValue: number;
  readonly sellable: boolean;
  readonly stackable: boolean;
  readonly maxStack: number;
  readonly icon: string;
  readonly accent: string;
  readonly accentGlow: string;
  /** Arbitrary metadata for future systems (upgrades, missions). */
  readonly tags?: readonly string[];
}

export const DEFAULT_MAX_STACK = 10;
export const MAX_STACK_SIZE = DEFAULT_MAX_STACK;

/** Default sell-value tiers per rarity. Items can override with explicit
 *  values when their economic role demands it (e.g., uranium is rare but
 *  also has a fuel use, so it's priced lower than aurora-crystal). Keep
 *  per-item explicit values close to these so the list reads coherently. */
export const RARITY_BASE_SELL_VALUE: Record<ItemRarity, number> = {
  common: 5,
  uncommon: 14,
  rare: 38,
  epic: 70
};

export function priceFromRarity(rarity: ItemRarity, modifier = 1): number {
  return Math.round(RARITY_BASE_SELL_VALUE[rarity] * modifier);
}

export const ITEMS: Record<ItemId, ItemDefinition> = {
  // ── Asteroid drops ──────────────────────────────────────────────────
  'gold-ore': {
    id: 'gold-ore',
    name: 'Gold ore',
    shortName: 'Gold',
    description: 'Crystalline and dense. Tradeable at any station in the sector.',
    category: 'ore',
    rarity: 'uncommon',
    origin: 'asteroid',
    sellValue: 18,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#f5c542',
    accentGlow: 'rgba(245, 197, 66, 0.55)',
    tags: ['tradeable'],
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="gold-g1" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#ffe690"/>
      <stop offset="0.5" stop-color="#f5c542"/>
      <stop offset="1" stop-color="#8a6213"/>
    </linearGradient>
    <radialGradient id="gold-g2" cx="0.35" cy="0.3" r="0.6">
      <stop offset="0" stop-color="#fff7c8" stop-opacity="0.95"/>
      <stop offset="1" stop-color="#fff7c8" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <polygon points="24,4 42,14 42,34 24,44 6,34 6,14"
           fill="url(#gold-g1)" stroke="#6a4d13" stroke-width="1.2"/>
  <polygon points="24,4 42,14 42,34 24,44 6,34 6,14"
           fill="url(#gold-g2)"/>
  <polyline points="12,16 24,10 36,16" fill="none" stroke="#fff2a8"
            stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>
  <polyline points="12,32 24,38 36,32" fill="none" stroke="#3f2a08"
            stroke-width="1" stroke-linecap="round" opacity="0.55"/>
</svg>`
  },
  'uranium-ore': {
    id: 'uranium-ore',
    name: 'Uranium ore',
    shortName: 'Uranium',
    description: 'Unstable radioactive fuel — refines into reactor energy.',
    category: 'ore',
    rarity: 'rare',
    origin: 'mixed',
    sellValue: 26,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#9aff3c',
    accentGlow: 'rgba(154, 255, 60, 0.55)',
    tags: ['fuel'],
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="ur-g1" cx="0.5" cy="0.4" r="0.7">
      <stop offset="0" stop-color="#dcff9a"/>
      <stop offset="0.5" stop-color="#9aff3c"/>
      <stop offset="1" stop-color="#2a5a0d"/>
    </radialGradient>
  </defs>
  <polygon points="24,4 42,14 42,34 24,44 6,34 6,14"
           fill="url(#ur-g1)" stroke="#1a3a05" stroke-width="1.2"/>
  <polygon points="24,10 34,28 14,28" fill="#b7ff6a" opacity="0.85"/>
</svg>`
  },
  'iron-scrap': {
    id: 'iron-scrap',
    name: 'Iron scrap',
    shortName: 'Iron',
    description: 'Common metal recovered from space wrecks.',
    category: 'material',
    rarity: 'common',
    origin: 'loot',
    sellValue: 5,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#b8c1d0',
    accentGlow: 'rgba(184, 193, 208, 0.45)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <polygon points="10,30 22,10 38,18 34,38 16,40" fill="#8e98a6" stroke="#3a404a" stroke-width="1.2"/>
  <polyline points="14,30 22,18 32,22 30,34" fill="none" stroke="#d3d9e3" stroke-width="1.2" opacity="0.7"/>
</svg>`
  },
  'alien-alloy': {
    id: 'alien-alloy',
    name: 'Alien alloy',
    shortName: 'Alloy',
    description:
      'Light and resistant material of unknown origin. Highly coveted.',
    category: 'material',
    rarity: 'epic',
    origin: 'loot',
    sellValue: 64,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#b48cff',
    accentGlow: 'rgba(180, 140, 255, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="alloy-g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#d6bfff"/>
      <stop offset="1" stop-color="#6638b3"/>
    </linearGradient>
  </defs>
  <polygon points="24,6 40,20 32,42 16,42 8,20" fill="url(#alloy-g)" stroke="#2a1352" stroke-width="1.2"/>
  <polyline points="16,20 24,14 32,20" fill="none" stroke="#f1e5ff" stroke-width="1.2" opacity="0.8"/>
</svg>`
  },

  // ── New asteroid resources ─────────────────────────────────────────
  'raw-quartz': {
    id: 'raw-quartz',
    name: 'Raw quartz',
    shortName: 'Quartz',
    description: 'Glassy mineral common in standard asteroids and many planet crusts.',
    category: 'ore',
    rarity: 'common',
    origin: 'mixed',
    sellValue: 4,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#cfe7ff',
    accentGlow: 'rgba(207, 231, 255, 0.45)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="quartz-g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#f4faff"/>
      <stop offset="1" stop-color="#7baad6"/>
    </linearGradient>
  </defs>
  <polygon points="24,4 38,16 32,42 16,42 10,16" fill="url(#quartz-g)" stroke="#3b6088" stroke-width="1.1"/>
  <polyline points="16,16 24,10 32,16 24,40" fill="none" stroke="#fff" stroke-width="1.1" opacity="0.7"/>
</svg>`
  },
  'ion-ice': {
    id: 'ion-ice',
    name: 'Ionised ice',
    shortName: 'Ion ice',
    description: 'Frozen volatile lattice charged by stellar radiation.',
    category: 'ore',
    rarity: 'uncommon',
    origin: 'asteroid',
    sellValue: 13,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#7ff3ff',
    accentGlow: 'rgba(127, 243, 255, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="ice-g" cx="0.5" cy="0.4" r="0.7">
      <stop offset="0" stop-color="#e6fdff"/>
      <stop offset="1" stop-color="#1f7c9c"/>
    </radialGradient>
  </defs>
  <polygon points="24,6 40,18 36,40 12,40 8,18" fill="url(#ice-g)" stroke="#0d3a4d" stroke-width="1.1"/>
  <line x1="24" y1="10" x2="24" y2="40" stroke="#fff" stroke-width="1.2" opacity="0.7"/>
  <line x1="10" y1="20" x2="38" y2="34" stroke="#fff" stroke-width="0.9" opacity="0.55"/>
  <line x1="10" y1="34" x2="38" y2="20" stroke="#fff" stroke-width="0.9" opacity="0.55"/>
</svg>`
  },
  'plasma-crystal': {
    id: 'plasma-crystal',
    name: 'Plasma crystal',
    shortName: 'Plasma',
    description: 'Lattice that holds compressed plasma. Powers high-end systems.',
    category: 'ore',
    rarity: 'rare',
    origin: 'asteroid',
    sellValue: 36,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#ff66c8',
    accentGlow: 'rgba(255, 102, 200, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="plasma-g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#ffd0f4"/>
      <stop offset="0.6" stop-color="#ff66c8"/>
      <stop offset="1" stop-color="#5a1a4a"/>
    </linearGradient>
  </defs>
  <polygon points="24,4 36,18 30,44 18,44 12,18" fill="url(#plasma-g)" stroke="#3a0d2c" stroke-width="1.1"/>
  <polyline points="24,4 24,44" fill="none" stroke="#fff" stroke-width="1.2" opacity="0.7"/>
  <circle cx="24" cy="22" r="3" fill="#fff" opacity="0.85"/>
</svg>`
  },
  'stellar-iron': {
    id: 'stellar-iron',
    name: 'Stellar iron',
    shortName: 'Stellar Fe',
    description: 'Dense ferrous alloy formed under stellar pressure.',
    category: 'material',
    rarity: 'uncommon',
    origin: 'mixed',
    sellValue: 16,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#9fb6c8',
    accentGlow: 'rgba(159, 182, 200, 0.5)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="stellar-g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#dde6f0"/>
      <stop offset="1" stop-color="#3a4f63"/>
    </linearGradient>
  </defs>
  <polygon points="10,18 22,8 38,16 36,36 18,40" fill="url(#stellar-g)" stroke="#1a2330" stroke-width="1.2"/>
  <polyline points="14,20 22,16 30,22 28,32" fill="none" stroke="#fff" stroke-width="1" opacity="0.55"/>
</svg>`
  },

  // ── Planetary / lunar resources ─────────────────────────────────────
  'hydrogen': {
    id: 'hydrogen',
    name: 'Hydrogen',
    shortName: 'H₂',
    description: 'Compressed hydrogen pockets harvested from icy crusts.',
    category: 'ore',
    rarity: 'common',
    origin: 'planet',
    sellValue: 7,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#a5d6ff',
    accentGlow: 'rgba(165, 214, 255, 0.5)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <circle cx="16" cy="22" r="9" fill="#a5d6ff" stroke="#34638e" stroke-width="1.2"/>
  <circle cx="32" cy="26" r="9" fill="#cfe7ff" stroke="#34638e" stroke-width="1.2"/>
  <line x1="16" y1="22" x2="32" y2="26" stroke="#34638e" stroke-width="1.2"/>
</svg>`
  },
  'sodium': {
    id: 'sodium',
    name: 'Sodium',
    shortName: 'Na',
    description: 'Salt-like crystals stripped from arid planet flats.',
    category: 'ore',
    rarity: 'common',
    origin: 'planet',
    sellValue: 6,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#ffb585',
    accentGlow: 'rgba(255, 181, 133, 0.45)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="9" y="9" width="12" height="12" fill="#ffd9b8" stroke="#7a4521" stroke-width="1.2"/>
  <rect x="27" y="9" width="12" height="12" fill="#ffb585" stroke="#7a4521" stroke-width="1.2"/>
  <rect x="9" y="27" width="12" height="12" fill="#ffb585" stroke="#7a4521" stroke-width="1.2"/>
  <rect x="27" y="27" width="12" height="12" fill="#ffd9b8" stroke="#7a4521" stroke-width="1.2"/>
</svg>`
  },
  'silver': {
    id: 'silver',
    name: 'Silver',
    shortName: 'Silver',
    description: 'Lustrous silver veins extracted from metallic crusts.',
    category: 'ore',
    rarity: 'uncommon',
    origin: 'planet',
    sellValue: 12,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#dde6ee',
    accentGlow: 'rgba(221, 230, 238, 0.5)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="silver-g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#fbfdff"/>
      <stop offset="0.5" stop-color="#dde6ee"/>
      <stop offset="1" stop-color="#7a838d"/>
    </linearGradient>
  </defs>
  <polygon points="10,28 22,10 38,18 34,38 16,38" fill="url(#silver-g)" stroke="#3c4148" stroke-width="1.2"/>
  <polyline points="14,28 22,16 32,22 30,34" fill="none" stroke="#fff" stroke-width="1.2" opacity="0.6"/>
</svg>`
  },
  'aurora-crystal': {
    id: 'aurora-crystal',
    name: 'Aurora crystal',
    shortName: 'Aurora',
    description:
      'Translucent shards that catch starlight in shifting bands of colour.',
    category: 'ore',
    rarity: 'rare',
    origin: 'planet',
    sellValue: 44,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#7ef0c8',
    accentGlow: 'rgba(126, 240, 200, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="aurora-g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="#7ef0c8"/>
      <stop offset="0.5" stop-color="#6cb6ff"/>
      <stop offset="1" stop-color="#b48cff"/>
    </linearGradient>
  </defs>
  <polygon points="24,4 36,12 38,30 24,44 10,30 12,12" fill="url(#aurora-g)" stroke="#1f3a55" stroke-width="1.1"/>
  <polyline points="24,4 24,44" fill="none" stroke="#fff" stroke-width="1.1" opacity="0.7"/>
  <polyline points="12,16 36,28" fill="none" stroke="#fff" stroke-width="0.9" opacity="0.6"/>
</svg>`
  },
  'amber-core': {
    id: 'amber-core',
    name: 'Amber core',
    shortName: 'Amber',
    description: 'Heat-fused resin core, formed deep inside hot worlds.',
    category: 'ore',
    rarity: 'rare',
    origin: 'planet',
    sellValue: 38,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#ffb24b',
    accentGlow: 'rgba(255, 178, 75, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="amber-g" cx="0.45" cy="0.4" r="0.65">
      <stop offset="0" stop-color="#ffe2a8"/>
      <stop offset="0.6" stop-color="#ffb24b"/>
      <stop offset="1" stop-color="#5a2c0d"/>
    </radialGradient>
  </defs>
  <ellipse cx="24" cy="24" rx="16" ry="18" fill="url(#amber-g)" stroke="#3a1a08" stroke-width="1.2"/>
  <circle cx="20" cy="20" r="3" fill="#fff" opacity="0.6"/>
  <circle cx="28" cy="28" r="2" fill="#fff" opacity="0.4"/>
</svg>`
  },
  'living-rock': {
    id: 'living-rock',
    name: 'Living rock',
    shortName: 'Living',
    description:
      'Mineral growth from biological worlds — pulses faintly when intact.',
    category: 'ore',
    rarity: 'rare',
    origin: 'planet',
    sellValue: 42,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#7fff8a',
    accentGlow: 'rgba(127, 255, 138, 0.55)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="living-g" cx="0.5" cy="0.5" r="0.6">
      <stop offset="0" stop-color="#dcffd9"/>
      <stop offset="0.55" stop-color="#7fff8a"/>
      <stop offset="1" stop-color="#1d4f24"/>
    </radialGradient>
  </defs>
  <polygon points="10,30 18,10 32,12 38,28 28,42 14,40" fill="url(#living-g)" stroke="#163c1c" stroke-width="1.2"/>
  <circle cx="22" cy="24" r="3.5" fill="#dfffd2" opacity="0.85"/>
  <circle cx="30" cy="32" r="2.2" fill="#fff" opacity="0.6"/>
</svg>`
  },
  'lumen-dust': {
    id: 'lumen-dust',
    name: 'Lumen dust',
    shortName: 'Lumen',
    description:
      'Microscopic photophores. Glows in cargo holds. Vanishingly rare.',
    category: 'ore',
    rarity: 'epic',
    origin: 'planet',
    sellValue: 78,
    sellable: true,
    stackable: true,
    maxStack: DEFAULT_MAX_STACK,
    accent: '#fff4a3',
    accentGlow: 'rgba(255, 244, 163, 0.65)',
    icon: `
<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <radialGradient id="lumen-g" cx="0.5" cy="0.5" r="0.6">
      <stop offset="0" stop-color="#fff8c5"/>
      <stop offset="0.6" stop-color="#fff4a3"/>
      <stop offset="1" stop-color="#7a6a1a"/>
    </radialGradient>
  </defs>
  <circle cx="24" cy="24" r="14" fill="url(#lumen-g)" stroke="#3a2f08" stroke-width="1"/>
  <circle cx="14" cy="16" r="2" fill="#fff"/>
  <circle cx="32" cy="14" r="1.5" fill="#fff"/>
  <circle cx="34" cy="32" r="2.4" fill="#fff" opacity="0.85"/>
  <circle cx="18" cy="34" r="1.3" fill="#fff" opacity="0.7"/>
</svg>`
  }
};

export const ITEM_ORDER: readonly ItemId[] = [
  'aurora-crystal',
  'living-rock',
  'amber-core',
  'plasma-crystal',
  'lumen-dust',
  'alien-alloy',
  'uranium-ore',
  'gold-ore',
  'silver',
  'stellar-iron',
  'ion-ice',
  'hydrogen',
  'sodium',
  'raw-quartz',
  'iron-scrap'
];

export function getItem(id: ItemId): ItemDefinition {
  return ITEMS[id];
}

export function listSellableItems(): ItemDefinition[] {
  return ITEM_ORDER.map((id) => ITEMS[id]).filter((i) => i.sellable);
}

/** Group helpers — handy for shop UI sections, loot tables, etc. */
export function listItemsByOrigin(origin: ItemOrigin): ItemDefinition[] {
  return ITEM_ORDER.map((id) => ITEMS[id]).filter(
    (i) => i.origin === origin || i.origin === 'mixed'
  );
}

export function listItemsByRarity(rarity: ItemRarity): ItemDefinition[] {
  return ITEM_ORDER.map((id) => ITEMS[id]).filter((i) => i.rarity === rarity);
}
